import { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import publicApiClient from '@services/api/publicClient'
import likesCommentsService from '@services/api/likesComments'
import apiClient from '@services/api/client'
import { useAuthStore } from '@store/authStore'
import toast from 'react-hot-toast'
import avatarPlaceholder from '@assets/images/image_01.png'
import { storageURL, mediaUrl } from '@config'
import { encodeId, decodeId } from '@utils/idCipher'

const CONDITION_LABELS = {
  neuf: 'Neuf', scelle: 'Scellé', reconditionne: 'Reconditionné',
  quasi_neuf: 'Quasi neuf', excellent: 'Excellent', bon: 'Bon état',
  moyen: 'État moyen', usage: 'Usagé',
}

// Prix affiché (deal) + ancien prix (barré). Gère la convention actuelle
// (prix=original, new_price=prix réduit, marge_reduite=%) ET l'ancienne (new_price=ancien prix).
const resolvePricing = (raw) => {
  const prixNum     = parseFloat(raw?.prix) || 0
  const newPriceNum = parseFloat(raw?.new_price) || 0
  const marge       = parseInt(String(raw?.marge_reduite ?? '').replace(/[^0-9]/g, '')) || 0
  if (marge > 0 && newPriceNum > 0 && newPriceNum < prixNum) {
    return { price: newPriceNum, oldPrice: prixNum }
  }
  if (newPriceNum > prixNum && newPriceNum > 0) {
    return { price: prixNum, oldPrice: newPriceNum }
  }
  return { price: prixNum, oldPrice: null }
}

function ProductDetailPage() {
  const { id: encodedId } = useParams()
  const id = decodeId(encodedId) // id réel utilisé pour les appels API
  const navigate = useNavigate()
  const { user, isAuthenticated } = useAuthStore()
  const [product, setProduct] = useState(null)
  const [loading, setLoading] = useState(true)
  const [selectedImage, setSelectedImage] = useState(0)
  const [relatedProducts, setRelatedProducts] = useState([])
  const [isLiked, setIsLiked] = useState(false)
  const [likes, setLikes] = useState(0)
  const [comments, setComments] = useState([])
  const [newComment, setNewComment] = useState('')
  const [showComments, setShowComments] = useState(true)
  const [loadingComments, setLoadingComments] = useState(false)
  const [isSubscribed, setIsSubscribed] = useState(false)
  const incrementedIdRef = useRef(null)
  const [subscribersCount, setSubscribersCount] = useState(0)
  const [relatedPage, setRelatedPage] = useState(0)
  const [perView, setPerView] = useState(5)

  useEffect(() => {
    const update = () => {
      if (window.innerWidth >= 1024) setPerView(5)
      else if (window.innerWidth >= 640) setPerView(3)
      else setPerView(2)
    }
    update()
    window.addEventListener('resize', update)
    return () => window.removeEventListener('resize', update)
  }, [])

  useEffect(() => { setRelatedPage(0) }, [perView])

  // Fonction pour formater les prix en FCFA
  const formatPrice = (price) => {
    const priceNum = typeof price === 'string' ? parseFloat(price.replace(/[^0-9]/g, '')) : price
    if (!priceNum || priceNum === 0) return ''
    return new Intl.NumberFormat('fr-FR').format(priceNum) + ' FCFA'
  }

  // Images du produit depuis le tableau images
  const productImages = product?.images && product.images.length > 0 
    ? product.images 
    : [product?.image, product?.image, product?.image]

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        setLoading(true)
        const res = await publicApiClient.get(`/posts/${id}`)
        const raw = res.data || res

        // Normaliser les champs backend → frontend
        const conditionKey = raw.etat_appareil || ''

        const pricing = resolvePricing(raw)
        const productData = {
          id:                  raw.post_id,
          name:                raw.libele,
          price:               pricing.price,
          oldPrice:            pricing.oldPrice,
          description:         raw.description,
          location:            raw.siteWeb,
          category_id:         raw.post_categorie_id,
          views:               raw.vues || 0,
          likes:               raw.post_likes_count || raw.likes || 0,
          etat_appareil:       conditionKey,
          etat_appareil_label: CONDITION_LABELS[conditionKey] || '',
          images:              raw.fichiers?.map(f => mediaUrl(f.chemin)) || [],
          image:               (() => { const f = raw.fichiers?.find(f => f.is_main) ?? raw.fichiers?.[0]; return f?.chemin ? mediaUrl(f.chemin) : null })(),
          client: raw.client ? {
            id:    raw.client.client_id,
            name:  [raw.client.nom, raw.client.prenom].filter(Boolean).join(' '),
            phone: raw.client.numero,
            photo: raw.client.photo_profil
                     ? (raw.client.photo_profil.startsWith('http') ? raw.client.photo_profil : mediaUrl(raw.client.photo_profil))
                     : null,
          } : null,
        }

        setProduct(productData)
        setLikes(productData.likes)

        // Appels nécessitant une connexion
        if (user?.client_id || user?.id) {
          const clientId = user?.id || user?.client_id

          // Vérifier si l'utilisateur a déjà liké
          try {
            const likeStatus = await likesCommentsService.checkLike(id, clientId)
            setIsLiked(likeStatus.isLiked || false)
          } catch (err) {
            console.error('Erreur vérification like:', err)
          }

          // Vérifier le statut d'abonnement (localStorage)
          if (productData?.client?.id) {
            const subKey = `sub_${clientId}_${productData.client.id}`
            setIsSubscribed(localStorage.getItem(subKey) === 'true')
          }
        }

        // Commentaires (public)
        try {
          const commentsData = await likesCommentsService.getComments(id)
          if (commentsData.success) {
            setComments(commentsData.comments || [])
          }
        } catch (err) {
          console.error('Erreur commentaires:', err)
        }

        // Récupérer les produits similaires
        if (productData?.category_id) {
          try {
            const data = await publicApiClient.get('/posts', {
              params: { post_categorie_id: productData.category_id, type: 2, status: 2 }
            })
            const items = data.data || data.posts || (Array.isArray(data) ? data : [])
            const related = items
              .filter(p => p.post_id !== productData.id)
              .slice(0, 20)
              .map(p => ({
                id:       p.post_id,
                name:     p.libele,
                ...resolvePricing(p),
                image:    (() => { const f = p.fichiers?.find(f => f.is_main) ?? p.fichiers?.[0]; return f?.chemin ? mediaUrl(f.chemin) : null })(),
                views:    p.vues || 0,
                likes:    p.post_likes_count ?? p.likes ?? 0,
                comments: p.post_comments_count ?? p.comments ?? 0,
                date:     p.created_at ? new Date(p.created_at).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }) : '',
                etat_appareil: p.etat_appareil || '',
              }))
            setRelatedProducts(related)
          } catch {}
        }
      } catch (err) {
        console.error('Erreur lors du chargement du produit:', err)
        setProduct(null)
      } finally {
        setLoading(false)
      }
    }

    if (id) {
      fetchProduct()
    }
  }, [id])

  // Incrément de vue — effet séparé avec guard pour éviter le double-appel (React StrictMode)
  useEffect(() => {
    if (!id || incrementedIdRef.current === id) return
    incrementedIdRef.current = id
    publicApiClient.post(`/posts/${id}/view`).catch(() => {})
  }, [id])

  const handleLike = async () => {
    // Like libre sans connexion — optimiste pour les invités
    if (!isAuthenticated) {
      setIsLiked(prev => !prev)
      setLikes(prev => isLiked ? prev - 1 : prev + 1)
      return
    }
    const clientId = user?.id || user?.client_id
    try {
      if (isLiked) {
        const result = await likesCommentsService.unlikePost(id, clientId)
        if (result.success) { setLikes(result.likes); setIsLiked(false) }
      } else {
        const result = await likesCommentsService.likePost(id, clientId)
        if (result.success) { setLikes(result.likes); setIsLiked(true) }
      }
    } catch (error) {
      console.error('Erreur lors du like:', error)
    }
  }

  const handleAddComment = async () => {
    if (!newComment.trim()) return
    // Connexion obligatoire pour commenter
    if (!isAuthenticated) {
      navigate('/login')
      return
    }
    const clientId = user?.id || user?.client_id
    setLoadingComments(true)
    try {
      const result = await likesCommentsService.addComment(id, newComment, clientId)
      if (result.success) {
        setComments([result.comment, ...comments])
        setNewComment('')
        if (product) setProduct({...product, comments: (product.comments || 0) + 1})
      }
    } catch (error) {
      console.error('Erreur lors de l\'ajout du commentaire:', error)
    } finally {
      setLoadingComments(false)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#D4AF37]"></div>
      </div>
    )
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-700 mb-4">Produit non trouvé</h2>
          <button
            onClick={() => navigate(-1)}
            className="px-6 py-2 bg-[#D4AF37] text-white rounded-lg hover:bg-[#c09d2a]"
          >
            Retour
          </button>
        </div>
      </div>
    )
  }

  // Calculer le pourcentage de réduction
  const priceNum = typeof product.price === 'string' ? parseFloat(product.price.replace(/[^0-9]/g, '')) : product.price
  const oldPriceNum = product.oldPrice ? (typeof product.oldPrice === 'string' ? parseFloat(product.oldPrice.replace(/[^0-9]/g, '')) : product.oldPrice) : 0
  const discount = oldPriceNum > 0 ? Math.round((1 - priceNum / oldPriceNum) * 100) : 0

  return (
    <div className="min-h-screen bg-gray-100">

      <div className="w-full md:w-[80%] mx-auto px-2 sm:px-4 py-3 sm:py-8">
        {/* Contenu principal */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Colonne gauche - Images */}
            <div>

              {/* Image principale */}
              <div className="relative mb-4 bg-white rounded-xl overflow-hidden h-[400px] md:h-[500px] flex items-center justify-center">
                {discount > 0 && (
                  <span className="absolute top-4 left-4 bg-red-600 text-white text-sm font-bold px-3 py-1 rounded z-10">
                    -{discount}%
                  </span>
                )}
                <img
                  src={productImages[selectedImage] || product.image}
                  alt={product.name}
                  className="w-full h-full object-contain"
                />
              </div>

              {/* Miniatures */}

              <div className="flex gap-3 overflow-x-auto pb-2">
                {productImages.map((img, index) => (
                  <button
                    key={index}
                    onClick={() => setSelectedImage(index)}
                    className={`flex-shrink-0 w-32 h-32 border-2 rounded-lg overflow-hidden bg-white flex items-center justify-center p-2 ${
                      selectedImage === index ? 'border-[#D4AF37]' : 'border-gray-200'
                    }`}
                  >
                    <img
                      src={img || product.image}
                      alt={`${product.name} ${index + 1}`}
                      className="max-w-full max-h-full object-contain"
                    />
                  </button>
                ))}
              </div>
            </div>

            {/* Colonne droite - Informations */}
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-[#001F3F] mb-4">
                {product.name}
              </h1>

              {/* Prix */}
              <div className="mb-4">
                {product.oldPrice && (
                  <p className="text-gray-400 line-through text-lg mb-1">{formatPrice(product.oldPrice)}</p>
                )}
                <p className="text-red-600 font-bold text-3xl">{formatPrice(product.price)}</p>
              </div>

              {/* État + Prix barré */}
              <div className="flex items-center gap-3 flex-wrap mb-4">
                {product.etat_appareil_label && (
                  <span className={`px-3 py-1 rounded-full text-sm font-bold ${
                    product.etat_appareil === 'neuf'          ? 'bg-blue-100 text-blue-700' :
                    product.etat_appareil === 'scelle'        ? 'bg-green-100 text-green-700' :
                    product.etat_appareil === 'quasi_neuf'    ? 'bg-teal-100 text-teal-700' :
                    product.etat_appareil === 'excellent'     ? 'bg-indigo-100 text-indigo-700' :
                    product.etat_appareil === 'bon'           ? 'bg-yellow-100 text-yellow-700' :
                    product.etat_appareil === 'reconditionne' ? 'bg-purple-100 text-purple-700' :
                    'bg-gray-100 text-gray-700'
                  }`}>
                    {product.etat_appareil_label}
                  </span>
                )}
                {discount > 0 && (
                  <span className="px-3 py-1 rounded-full text-sm font-bold bg-red-100 text-red-600">
                    -{discount}% de réduction
                  </span>
                )}
              </div>

              {/* Détails */}
              <div className="mb-6">
                <h2 className="font-bold text-lg mb-3 flex items-center gap-2">
                  Détails
                  {product.views > 0 && (
                    <div className="flex items-center gap-1">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <svg key={star} className="w-4 h-4 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                        </svg>
                      ))}
                      <span className="text-sm text-gray-600 ml-2">{product.views} {product.views === 1 ? 'vue' : 'vues'}</span>
                    </div>
                  )}
                </h2>
                {product.description && (
                  <div className="max-h-[260px] overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-gray-300">
                    <p className="text-gray-700 leading-relaxed text-sm whitespace-pre-line">
                      {product.description}
                    </p>
                  </div>
                )}
              </div>

              {/* Localisation */}
              {product.location && (
                <div className="bg-[#FFF4CC] rounded-lg p-4 mb-4">
                  <div className="flex items-center gap-2">
                    <svg className="w-5 h-5 text-[#D4AF37] flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                    </svg>
                    <p className="text-sm text-gray-800">{product.location}</p>
                  </div>
                </div>
              )}

              {/* Contact — visible pour tous */}
              {product.client && (
                <div className="bg-[#FFF4CC] rounded-lg p-4 mb-6">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div className="flex items-center gap-3">
                    {/* Photo du vendeur */}
                    {(() => {
                      const rawPhoto = product.client?.photo || null
                      const photoUrl = rawPhoto
                        ? (rawPhoto.startsWith('http') ? rawPhoto : mediaUrl(rawPhoto))
                        : null
                      const name = product.client?.name || 'Vendeur'
                      const initials = name.split(' ').map(w => w[0]).filter(Boolean).join('').slice(0, 2).toUpperCase()
                      return photoUrl ? (
                        <img
                          src={photoUrl}
                          alt={name}
                          className="w-12 h-12 rounded-full object-cover border-2 border-[#D4AF37] flex-shrink-0"
                          onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex' }}
                        />
                      ) : null
                    })()}
                    {(() => {
                      const rawPhoto = product.client?.photo || null
                      const photoUrl = rawPhoto
                        ? (rawPhoto.startsWith('http') ? rawPhoto : mediaUrl(rawPhoto))
                        : null
                      const name = product.client?.name || 'Vendeur'
                      const initials = name.split(' ').map(w => w[0]).filter(Boolean).join('').slice(0, 2).toUpperCase()
                      return (
                        <div
                          className="w-12 h-12 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#c09d2a] flex items-center justify-center border-2 border-[#D4AF37] flex-shrink-0"
                          style={{ display: photoUrl ? 'none' : 'flex' }}
                        >
                          <span className="text-white text-base font-bold">{initials}</span>
                        </div>
                      )
                    })()}
                    {/* Infos du vendeur */}
                    <div className="flex flex-col gap-1">
                      <div className="font-semibold text-[#001F3F]">{product.client?.name || 'Vendeur'}</div>
                      <div className="text-sm text-gray-700">{product.client?.phone || 'Téléphone non disponible'}</div>
                    </div>
                  </div>
                  
                  {/* Boutons action — masqués pour le propriétaire */}
                  {(user?.id !== product.client?.id && user?.client_id !== product.client?.id) && (
                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      onClick={() => {
                        const phone = product.client?.phone;
                        if (phone) window.location.href = `tel:${phone}`;
                      }}
                      className="bg-[#D4AF37] text-white p-3 rounded-lg hover:bg-[#c09d2a] transition-colors"
                      title="Appeler"
                    >
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                      </svg>
                    </button>
                    <button
                      onClick={() => {
                        if (!isAuthenticated) { navigate('/login'); return }
                        navigate('/messages', {
                          state: {
                            openConversationWith: {
                              id:     product.client?.id,
                              name:   product.client?.name,
                              phone:  product.client?.phone,
                              avatar: product.client?.photo,
                              article: {
                                name:  product.name,
                                url:   `/product/${encodedId}`,
                                image: product.image,
                              }
                            }
                          }
                        });
                      }}
                      className="bg-[#D4AF37] text-white p-3 rounded-lg hover:bg-[#c09d2a] transition-colors"
                      title="Message"
                    >
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10c0 3.866-3.582 7-8 7a8.841 8.841 0 01-4.083-.98L2 17l1.338-3.123C2.493 12.767 2 11.434 2 10c0-3.866 3.582-7 8-7s8 3.134 8 7zM7 9H5v2h2V9zm8 0h-2v2h2V9zM9 9h2v2H9V9z" clipRule="evenodd" />
                      </svg>
                    </button>
                    <button
                      onClick={() => {
                        const phone = product.client?.phone || product.client_phone;
                        if (phone) {
                          let digits = phone.replace(/\D/g, '')
                          // Ajoute l'indicatif +225 si le numéro commence par 0 (Côte d'Ivoire)
                          if (digits.startsWith('0') && digits.length <= 10) digits = '225' + digits.slice(1)
                          window.open(`https://wa.me/${digits}`, '_blank')
                        }
                      }}
                      className="bg-[#D4AF37] text-white p-3 rounded-lg hover:bg-[#c09d2a] transition-colors" 
                      title="WhatsApp"
                    >
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                      </svg>
                    </button>
                    {/* Bouton S'abonner - Ne s'affiche pas pour le propriétaire de l'article */}
                    {(user?.id !== product.client?.id && user?.client_id !== product.client?.id) && (
                      <button
                        onClick={async () => {
                          if (!isAuthenticated) { navigate('/login'); return }
                          const clientId = user?.id || user?.client_id
                          const abonneId = product.client?.id
                          if (!abonneId) { alert('Impossible de trouver l\'ID du vendeur'); return }
                          try {
                            await apiClient.post('/subscription/subscribe', {
                              action: 'toggle',
                              client_id: clientId,
                              abonne_id: abonneId
                            })
                            const newState = !isSubscribed
                            setIsSubscribed(newState)
                            setSubscribersCount(prev => newState ? prev + 1 : prev - 1)
                            localStorage.setItem(`sub_${clientId}_${abonneId}`, newState)
                            toast.success(newState ? 'Abonné avec succès' : 'Désabonné avec succès')
                          } catch (error) {
                            toast.error('Erreur lors de l\'abonnement')
                          }
                        }}
                        className={`px-4 py-2 rounded-md transition-colors text-sm font-medium ${
                          isSubscribed 
                            ? 'bg-gray-400 text-white hover:bg-gray-500' 
                            : 'bg-[#D4AF37] text-white hover:bg-[#c09d2a]'
                        }`}
                        title={isSubscribed ? 'Se désabonner' : 'S\'abonner'}
                      >
                        {isSubscribed ? 'Abonné' : 'S\'abonner'}
                      </button>
                    )}
                  </div>
                  )}
                </div>
              </div>
              )}

            </div>
          </div>

          {/* Section Likes et Commentaires */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <div className="flex items-center gap-6 mb-6 pb-4 border-b">
              <button 
                onClick={handleLike}
                className={`flex items-center gap-2 transition-colors ${isLiked ? 'text-red-600' : 'text-gray-600 hover:text-red-600'}`}
              >
                <svg className="w-6 h-6" fill={isLiked ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
                <span className="font-semibold">{likes} J'aime</span>
              </button>

              <button 
                onClick={() => setShowComments(!showComments)}
                className="flex items-center gap-2 text-gray-600 hover:text-[#D4AF37] transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
                <span className="font-semibold">{comments.length} Commentaires</span>
              </button>
            </div>

            {showComments && (
              <div className="space-y-4">
                {/* Ajouter un commentaire */}
                {isAuthenticated ? (
                  <div className="flex gap-3">
                    {(() => {
                      const rawPhoto = user?.photo_profil || user?.photo || null
                      const photoUrl = rawPhoto
                        ? rawPhoto.startsWith('http') ? rawPhoto
                          : rawPhoto.startsWith('/storage/') ? `${storageURL.replace(/\/storage$/, '')}${rawPhoto}`
                          : mediaUrl(rawPhoto)
                        : null
                      const initials = [user?.nom || user?.name || '', user?.prenom || '']
                        .map(s => s.charAt(0)).filter(Boolean).join('').toUpperCase() || 'U'
                      return (
                        <div className="relative w-10 h-10 flex-shrink-0">
                          {photoUrl && (
                            <img
                              src={photoUrl}
                              alt={initials}
                              className="w-10 h-10 rounded-full object-cover absolute inset-0"
                              onError={e => { e.target.style.display = 'none' }}
                            />
                          )}
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#c09d2a] flex items-center justify-center">
                            <span className="text-white text-sm font-bold">{initials}</span>
                          </div>
                        </div>
                      )
                    })()}
                    <div className="flex-1">
                      <textarea
                        value={newComment}
                        onChange={(e) => setNewComment(e.target.value)}
                        placeholder="Écrivez un commentaire..."
                        className="w-full border border-gray-300 rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-[#D4AF37] resize-none"
                        rows="3"
                      />
                      <button
                        onClick={handleAddComment}
                        disabled={loadingComments || !newComment.trim()}
                        className="mt-2 bg-[#D4AF37] text-white px-6 py-2 rounded-lg hover:bg-[#c09d2a] transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {loadingComments ? 'Publication...' : 'Publier'}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                    <svg className="w-5 h-5 text-[#D4AF37] flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    <span className="text-gray-600 text-sm">
                      <button onClick={() => navigate('/login')} className="text-[#D4AF37] font-semibold hover:underline">Connectez-vous</button>
                      {' '}pour laisser un commentaire
                    </span>
                  </div>
                )}

                {/* Liste des commentaires */}
                <div className="space-y-4 mt-6">
                  {comments.length === 0 ? (
                    <p className="text-gray-500 text-center py-8">Aucun commentaire pour le moment. Soyez le premier à commenter !</p>
                  ) : (
                    comments.map((comment) => {
                      const clientName = comment.client?.name || comment.author || 'Anonyme'
                      const clientPhoto = comment.client?.photo
                        ? (comment.client.photo.startsWith('http') ? comment.client.photo : mediaUrl(comment.client.photo))
                        : null
                      const initials = clientName.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()
                      const commentText = comment.comment_text || comment.text || ''
                      const commentDate = comment.created_at
                        ? new Date(comment.created_at).toLocaleDateString('fr-FR')
                        : comment.date || ''
                      return (
                        <div key={comment.id} className="flex gap-3 p-4 bg-gray-50 rounded-lg">
                          {clientPhoto ? (
                            <img src={clientPhoto} alt={clientName} className="w-10 h-10 rounded-full object-cover flex-shrink-0" />
                          ) : (
                            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#c09d2a] flex items-center justify-center flex-shrink-0">
                              <span className="text-white text-sm font-bold">{initials}</span>
                            </div>
                          )}
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-semibold text-[#001F3F]">{clientName}</span>
                              <span className="text-xs text-gray-500">{commentDate}</span>
                            </div>
                            <p className="text-gray-700">{commentText}</p>
                          </div>
                        </div>
                      )
                    })
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Produits similaires — carousel */}
        {relatedProducts.length > 0 && (() => {
          const totalPages = Math.ceil(relatedProducts.length / perView)
          const cardWidthPct = 100 / perView
          return (
            <div className="bg-white rounded-xl shadow-sm p-4 sm:p-6">
              <h2 className="text-lg sm:text-2xl font-bold text-[#001F3F] mb-4">Produits similaires</h2>

              <div className="relative">
                {/* Flèche gauche */}
                {relatedPage > 0 && (
                  <button
                    onClick={() => setRelatedPage(p => p - 1)}
                    className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 z-10 w-9 h-9 rounded-full bg-white shadow-md border border-gray-200 flex items-center justify-center hover:bg-gray-50 transition-colors"
                  >
                    <svg className="w-4 h-4 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7"/>
                    </svg>
                  </button>
                )}

                {/* Track du carousel */}
                <div className="overflow-hidden">
                  <div
                    className="flex transition-transform duration-300 ease-in-out"
                    style={{ transform: `translateX(-${relatedPage * 100}%)` }}
                  >
                    {relatedProducts.map(p => {
                      const pn  = parseFloat(String(p.price).replace(/[^0-9.]/g, '')) || 0
                      const opn = parseFloat(String(p.oldPrice).replace(/[^0-9.]/g, '')) || 0
                      const disc = opn > pn ? Math.round((1 - pn / opn) * 100) : 0
                      return (
                        <div
                          key={p.id}
                          style={{ width: `${cardWidthPct}%`, flexShrink: 0, padding: '0 6px' }}
                        >
                          <div
                            onClick={() => { navigate(`/product/${encodeId(p.id)}`); window.scrollTo(0, 0) }}
                            className="bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-all duration-300 hover:-translate-y-1 border border-gray-100 flex flex-col cursor-pointer h-full"
                          >
                            <div className="relative h-52 flex-shrink-0">
                              <img src={p.image} alt={p.name} className="w-full h-full object-cover" onError={e => e.target.style.display='none'} />
                              {disc > 0 && (
                                <span className="absolute top-2 left-2 bg-red-600 text-white text-xs font-bold px-1.5 py-0.5 rounded">-{disc}%</span>
                              )}
                              {p.etat_appareil && (
                                <span className="absolute top-2 right-2 bg-blue-600 text-white text-xs font-bold px-1.5 py-0.5 rounded">
                                  {CONDITION_LABELS[p.etat_appareil] || p.etat_appareil}
                                </span>
                              )}
                              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-2">
                                <div className="flex items-center gap-2 text-white text-xs">
                                  <span className="flex items-center gap-0.5">
                                    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20"><path d="M10 12a2 2 0 100-4 2 2 0 000 4z"/><path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd"/></svg>
                                    {p.views}
                                  </span>
                                  <span className="flex items-center gap-0.5">
                                    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z" clipRule="evenodd"/></svg>
                                    {p.likes}
                                  </span>
                                  <span className="flex items-center gap-0.5">
                                    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M18 10c0 3.866-3.582 7-8 7a8.841 8.841 0 01-4.083-.98L2 17l1.338-3.123C2.493 12.767 2 11.434 2 10c0-3.866 3.582-7 8-7s8 3.134 8 7z" clipRule="evenodd"/></svg>
                                    {p.comments}
                                  </span>
                                </div>
                              </div>
                            </div>
                            <div className="p-2 flex-1 flex flex-col">
                              <h4 className="font-semibold text-black text-xs mb-1 line-clamp-1">{p.name}</h4>
                              {p.oldPrice && <p className="text-gray-400 line-through text-xs">{formatPrice(p.oldPrice)}</p>}
                              <p className="text-red-600 font-bold text-sm">{formatPrice(p.price)}</p>
                              <p className="text-xs text-gray-400 mt-auto pt-1 border-t">{p.date}</p>
                            </div>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>

                {/* Flèche droite */}
                {relatedPage < totalPages - 1 && (
                  <button
                    onClick={() => setRelatedPage(p => p + 1)}
                    className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 z-10 w-9 h-9 rounded-full bg-white shadow-md border border-gray-200 flex items-center justify-center hover:bg-gray-50 transition-colors"
                  >
                    <svg className="w-4 h-4 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7"/>
                    </svg>
                  </button>
                )}

                {/* Dots */}
                {totalPages > 1 && (
                  <div className="flex justify-center gap-1.5 mt-4">
                    {Array.from({ length: totalPages }).map((_, i) => (
                      <button
                        key={i}
                        onClick={() => setRelatedPage(i)}
                        className={`h-2 rounded-full transition-all duration-300 ${i === relatedPage ? 'bg-[#D4AF37] w-5' : 'bg-gray-300 w-2'}`}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
          )
        })()}
      </div>
    </div>
  )
}

export default ProductDetailPage
